import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Search, UploadCloud, UserCircle, Bot, CheckSquare, RefreshCw, ArrowRight } from 'lucide-react';
import { MrAvatar } from '../components/MrAvatar';
const steps = [{
  num: '01',
  title: 'Meet MR',
  icon: <Users className="w-8 h-8 text-teal" data-id="element-1726" />,
  desc: 'Your journey begins with a dedicated consultation. We introduce you to MISTR (MR), your AI Chief of Operations, and understand the high-level vision for your business automation.',
  link: '/how-it-works/meet-mr',
  details: ['Initial consultation and vision alignment', 'Introduction to the MR ecosystem', 'High-level capability overview']
}, {
  num: '02',
  title: 'Business Deep Dive',
  icon: <Search className="w-8 h-8 text-teal" data-id="element-1727" />,
  desc: 'We conduct a comprehensive analysis of your entire operation to identify exactly where AI can drive the most impact.',
  link: '/how-it-works/business-deep-dive',
  details: ['Services Offered', 'Marketing Services', 'Staff Roles and Needs', 'Goals', 'Problems', 'Competitors']
}, {
  num: '03',
  title: 'Intake/Upload All Data',
  icon: <UploadCloud className="w-8 h-8 text-teal" data-id="element-1728" />,
  desc: "We feed your business's historical and operational data into the system so your AI agents learn exactly how your company operates.",
  link: '/how-it-works/intake-upload',
  details: ['Recorded Phone Calls', 'Zoom Meetings/Google Meet Recordings', 'Staff Email History', 'Slack/Chat Messages', 'Phone Calls', 'SMS Messages']
}, {
  num: '04',
  title: 'Create Brand Persona',
  icon: <UserCircle className="w-8 h-8 text-teal" data-id="element-1729" />,
  desc: "Using the ingested data, we craft a unique, consistent brand voice and persona. Your AI agents won't just sound like bots—they will sound exactly like your best employees, embodying your brand's core values and tone.",
  link: '/how-it-works/brand-persona',
  details: []
}, {
  num: '05',
  title: 'Create & Implement Agents',
  icon: <Bot className="w-8 h-8 text-teal" data-id="element-1730" />,
  desc: 'We deploy specialized AI agents across your organization. From customer service and sales to internal project management and marketing, each agent is custom-built to handle specific roles with precision and autonomy.',
  link: '/how-it-works/create-agents',
  details: []
}, {
  num: '06',
  title: 'Review The Process',
  icon: <CheckSquare className="w-8 h-8 text-teal" data-id="element-1731" />,
  desc: "We don't just set it and forget it. We rigorously test and review the implemented agents, monitoring their interactions and workflows to ensure they meet our strict quality standards and your operational expectations.",
  link: '/how-it-works/review-process',
  details: []
}, {
  num: '07',
  title: 'Revamp & Repeat',
  icon: <RefreshCw className="w-8 h-8 text-teal" data-id="element-1732" />,
  desc: 'Business evolves, and so should your AI. We continuously analyze performance data, refine agent behaviors, and expand capabilities to ensure your automation scales seamlessly with your growth.',
  link: '/how-it-works/revamp-repeat',
  details: []
}];
export function HowItWorksPage() {
  return <main className="pt-[150px] bg-white min-h-screen" data-id="element-1733">
      {/* Hero Section */}
      <section className="relative pt-20 pb-24 px-6 bg-hero-gradient overflow-hidden text-center" data-id="element-1734">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1735" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1736" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-1737">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex justify-center mb-10" data-id="element-1738">
            <MrAvatar size={160} expression="confident" data-id="element-1739" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-teal text-sm font-medium mb-8" data-id="element-1740">
            The Journey
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-5xl md:text-7xl font-poppins font-extrabold text-white mb-6 leading-tight" data-id="element-1741">
            How It{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal to-purple" data-id="element-1742">
              Works
            </span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-steel max-w-2xl mx-auto" data-id="element-1743">
            A proven 7-step methodology to transform your business from manual
            operations to a fully autonomous, AI-driven powerhouse.
          </motion.p>
        </div>
      </section>

      {/* Steps List */}
      <div className="max-w-5xl mx-auto" data-id="element-1744">
        {steps.map((step, index) => <section key={step.num} className={`py-24 px-6 relative ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="element-1745">
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-12 items-start" data-id="element-1746">
              {/* Step Number Indicator */}
              <div className="hidden md:flex flex-col items-center shrink-0 w-24" data-id="element-1747">
                <div className="w-16 h-16 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-2xl font-poppins font-bold text-teal mb-4" data-id="element-1748">
                  {step.num}
                </div>
                {index !== steps.length - 1 && <div className="w-px h-full min-h-[200px] bg-gradient-to-b from-teal/20 to-transparent" data-id="element-1749" />}
              </div>

              {/* Content */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} className="flex-1" data-id="element-1750">
                <div className="md:hidden inline-block px-3 py-1 bg-teal/10 text-teal font-bold rounded-lg mb-4 text-sm" data-id="element-1751">
                  Step {step.num}
                </div>

                <div className="flex items-center gap-4 mb-6" data-id="element-1752">
                  <div className="p-3 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-1753">
                    {step.icon}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-poppins font-bold text-navy-dark" data-id="element-1754">
                    {step.title}
                  </h2>
                </div>

                <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1755">
                  {step.desc}
                </p>

                {step.details.length > 0 && <div className="grid sm:grid-cols-2 gap-4 mb-8" data-id="element-1756">
                    {step.details.map((detail, i) => <div key={i} className="flex items-start gap-3" data-id="element-1757">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2 shrink-0" data-id="element-1758" />
                        <span className="text-gray-700 font-medium" data-id="element-1759">
                          {detail}
                        </span>
                      </div>)}
                  </div>}

                <Link to={step.link} className="inline-flex items-center gap-2 text-teal font-semibold hover:text-navy-dark transition-colors group" data-id="element-1760">
                  Explore this step
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-1761" />
                </Link>
              </motion.div>
            </div>
          </section>)}
      </div>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-navy-dark text-center relative overflow-hidden" data-id="element-1762">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-1763" />
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-1764">
          <h2 className="text-4xl font-poppins font-bold text-white mb-6" data-id="element-1765">
            Ready to start the process?
          </h2>
          <p className="text-xl text-steel mb-10" data-id="element-1766">
            Book your initial consultation and let's meet MR.
          </p>
          <Link to="/contact" className="btn-primary text-lg px-8 py-4" data-id="element-1767">
            Talk to MR
          </Link>
        </div>
      </section>
    </main>;
}